import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import CloudOffRoundedIcon from "@mui/icons-material/CloudOffRounded";

function OfflineFallback({ onReload, onBack, online, canGoBack }) {
    return (
        <Box
            sx={{
                minHeight: "100dvh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                px: 3,
            }}
        >
            <Stack spacing={2} alignItems="center" maxWidth={360}>
                <CloudOffRoundedIcon sx={{ fontSize: 56, color: "text.secondary" }} />
                <Typography variant="h6" fontWeight={700}>
                    This page isn't available offline yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {online
                        ? "Reload to load this page."
                        : "You're offline. Reconnect and this page will load automatically."}
                </Typography>
                {online ? (
                    <Button variant="contained" onClick={onReload} sx={{ borderRadius: 3, textTransform: "none" }}>
                        Reload
                    </Button>
                ) : (
                    canGoBack && (
                        <Button variant="contained" onClick={onBack} sx={{ borderRadius: 3, textTransform: "none" }}>
                            Go back
                        </Button>
                    )
                )}
            </Stack>
        </Box>
    );
}

// Catches failed lazy-chunk loads (offline / stale deploy) and shows a recoverable fallback.
// Reload on reconnect while the fallback is visible; lazyWithRetry handles later online visits.
export default class ChunkErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidMount() {
        this._onOnline = () => {
            if (this.state.hasError) window.location.reload();
        };
        window.addEventListener("online", this._onOnline);
    }

    componentWillUnmount() {
        if (this._onOnline) window.removeEventListener("online", this._onOnline);
    }

    handleReload = () => {
        window.location.reload();
    };

    handleBack = () => {
        window.history.back();
    };

    render() {
        if (this.state.hasError) {
            return (
                <OfflineFallback
                    onReload={this.handleReload}
                    onBack={this.handleBack}
                    online={window.navigator.onLine}
                    canGoBack={window.history.length > 1}
                />
            );
        }
        return this.props.children;
    }
}
